import { ChangeDetectionStrategy, Component, effect, ElementRef, inject, input, output, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-match-search',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './match-search.component.html',
  styleUrl: './match-search.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MatchSearchComponent {
  readonly expanded = input.required<boolean>();
  readonly query = input.required<string>();

  readonly toggleExpanded = output<void>();
  readonly queryChange = output<string>();

  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');
  private wasExpanded = false;

  constructor() {
    effect(() => {
      const expanded = this.expanded();
      const shouldAutoFocus = expanded && !this.wasExpanded;
      this.wasExpanded = expanded;

      if (!shouldAutoFocus) {
        return;
      }

      queueMicrotask(() => this.searchInput()?.nativeElement.focus());
    });
  }

  protected onInput(event: Event): void {
    this.queryChange.emit((event.target as HTMLInputElement).value);
  }

  protected onFocusOut(event: FocusEvent): void {
    if (!this.expanded() || this.query().trim().length > 0) {
      return;
    }

    const relatedTarget = event.relatedTarget as Node | null;
    if (relatedTarget && this.host.nativeElement.contains(relatedTarget)) {
      return;
    }

    this.toggleExpanded.emit();
  }

  protected onClearMouseDown(event: MouseEvent): void {
    event.preventDefault();
  }

  protected onClear(): void {
    this.queryChange.emit('');

    const activeElement = this.host.nativeElement.ownerDocument.activeElement;
    if (activeElement instanceof HTMLElement) {
      activeElement.blur();
    }

    if (this.expanded()) {
      this.toggleExpanded.emit();
    }
  }

  protected onEscape(event: KeyboardEvent): void {
    if (event.key !== 'Escape') {
      return;
    }

    event.preventDefault();
    if (this.query().trim().length > 0) {
      this.queryChange.emit('');
      return;
    }

    this.toggleExpanded.emit();
  }
}
